import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Eye, Pencil, FileDown, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useVariants, VariantSelect } from "../components/VariantPicker";

export default function Transfers() {
  const { can, canEdit } = useAuth();
  const navigate = useNavigate();
  const write = can("warehouse_admin") || canEdit("transfer_stock");
  const [list, setList] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [history, setHistory] = useState([]);
  const [autoTransfers, setAutoTransfers] = useState({ items: [], total_qty: 0 });
  const today = new Date().toISOString().slice(0, 10);
  const [histFilter, setHistFilter] = useState({ start: today, end: today, q: "", source: "ALL" });
  const [histPage, setHistPage] = useState(1);
  const HIST_PER_PAGE = 10;
  const [range, setRange] = useState({ start: "", end: "" });
  const [autoRange, setAutoRange] = useState({ start: today, end: today, q: "" });
  const [destFilter, setDestFilter] = useState("ALL");
  const [detail, setDetail] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadList = () => api.get("/transfers", { params: { start: range.start || undefined, end: range.end || undefined } }).then((r) => { setList(r.data); setPage(1); });
  const loadNeeds = () => api.get("/transfers/needs").then((r) => setNeeds(r.data)).catch(() => setNeeds([]));
  const loadHistory = () => api.get("/transfers/keep-history", { params: { start: histFilter.start || undefined, end: histFilter.end || undefined, q: histFilter.q || undefined, source: histFilter.source !== "ALL" ? histFilter.source : undefined } }).then((r) => { setHistory(r.data); setHistPage(1); }).catch(() => setHistory([]));
  const loadAutoTransfers = () => api.get("/transfers/auto-new-to-main", { params: { start: autoRange.start || undefined, end: autoRange.end || undefined, q: autoRange.q || undefined } }).then((r) => setAutoTransfers(r.data)).catch(() => setAutoTransfers({ items: [], total_qty: 0 }));
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [range]);
  useEffect(() => { loadNeeds(); }, []);
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [histFilter]);
  useEffect(() => { loadAutoTransfers(); /* eslint-disable-next-line */ }, [autoRange]);
  const reload = () => { loadList(); loadNeeds(); loadHistory(); loadAutoTransfers(); };

  const confirmPrepare = async (t) => {
    if (!window.confirm(`Tandai transfer ${t.number} SUDAH DISIAPKAN? Stok akan dipindah dari ${t.from_warehouse_name} ke ${t.to_warehouse_name} sekarang.`)) return;
    try { await api.post(`/transfers/${t.id}/confirm`); toast.success("Transfer disiapkan, stok dipindah"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const destGroups = useMemo(() => {
    const g = {};
    needs.forEach((n) => {
      const k = n.to_warehouse;
      (g[k] = g[k] || { to_warehouse: k, to_warehouse_name: n.to_warehouse_name, rows: [] }).rows.push(n);
    });
    return Object.values(g);
  }, [needs]);
  const destOptions = useMemo(() => destGroups.map((g) => ({ id: g.to_warehouse, name: g.to_warehouse_name })), [destGroups]);
  const visibleGroups = destFilter === "ALL" ? destGroups : destGroups.filter((g) => g.to_warehouse === destFilter);

  const histTotalPages = Math.max(1, Math.ceil(history.length / HIST_PER_PAGE));
  const histRows = history.slice((histPage - 1) * HIST_PER_PAGE, histPage * HIST_PER_PAGE);

  const processRefs = async (refs) => {
    if (!refs.length) return;
    try { const r = await api.post("/transfers/needs/fulfill", { refs }); toast.success(`${r.data.processed} item diproses`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (t) => {
    if (!window.confirm(`Hapus transfer ${t.number}? Stok akan dikembalikan.`)) return;
    try { await api.delete(`/transfers/${t.id}`); toast.success("Transfer dihapus"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doExport = async ({ mode, from, to, start, end }) => {
    try {
      const r = await api.get("/transfers/export", {
        params: { mode, from_warehouse: mode === "specific" ? from : undefined, to_warehouse: mode === "specific" ? to : undefined, start: start || undefined, end: end || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "rekap-pengiriman.xlsx"; a.click(); URL.revokeObjectURL(url);
      toast.success("Export Excel berhasil");
    } catch (e) { toast.error("Export gagal"); }
  };

  return (
    <div data-testid="transfers-page">
      <PageHeader title="Transfer Stock" subtitle="Kebutuhan transfer stok untuk memenuhi Keep + transfer manual antar gudang" />

      <Panel className="mb-5">
        <div className="px-4 py-3 border-b border-taupe/15 flex items-center justify-between flex-wrap gap-2">
          <span className="font-heading font-semibold text-cocoa">Kebutuhan Transfer ({needs.length})</span>
          {destOptions.length > 1 && (
            <div className="flex items-center gap-2 text-sm">
              <Label className="text-wmuted">Gudang Tujuan</Label>
              <Select value={destFilter} onValueChange={setDestFilter}>
                <SelectTrigger className="h-9 bg-white w-52" data-testid="need-dest-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua Gudang</SelectItem>
                  {destOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {!needs.length && <div className="px-4 py-8 text-center text-wmuted">Tidak ada kebutuhan transfer. Semua stok keep sudah di gudang tujuan.</div>}
        {!!needs.length && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-4">
            {visibleGroups.map((grp) => (
              <NeedPanel key={grp.to_warehouse} group={grp} write={write} onProcess={processRefs} />
            ))}
          </div>
        )}
      </Panel>

      <Panel className="mb-5" data-testid="auto-new-to-main-panel">
        <div className="flex items-center justify-between p-4 flex-wrap gap-3 border-b border-taupe/15">
          <div>
            <div className="font-heading font-semibold text-cocoa">Auto Transfer Gudang Baru → Gudang Utama</div>
            <div className="text-xs text-wmuted mt-0.5" data-testid="auto-new-to-main-total">
              {autoTransfers.items.length} varian · {autoTransfers.total_qty || 0} pcs dipindahkan
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Label className="text-wmuted">Dari</Label>
            <Input type="date" value={autoRange.start} onChange={(event) => setAutoRange({ ...autoRange, start: event.target.value })} className="h-9 bg-white w-40" data-testid="auto-transfer-start-date" />
            <Label className="text-wmuted">Sampai</Label>
            <Input type="date" value={autoRange.end} onChange={(event) => setAutoRange({ ...autoRange, end: event.target.value })} className="h-9 bg-white w-40" data-testid="auto-transfer-end-date" />
            <Input value={autoRange.q} onChange={(event) => setAutoRange({ ...autoRange, q: event.target.value })} placeholder="Cari produk..." className="h-9 bg-white w-44" data-testid="auto-transfer-search" />
          </div>
        </div>
        <div className="space-y-2 p-3 md:hidden" data-testid="auto-transfer-mobile-list">
          {autoTransfers.items.map((item) => (
            <div
              key={`${item.variant_id}-${item.product_id}`}
              className="border border-taupe/20 rounded-lg p-3"
              data-testid={`auto-transfer-mobile-row-${item.variant_id}`}
            >
              <div className="font-medium text-cocoa">{item.product_name}</div>
              <div className="text-xs text-wmuted mt-0.5">{item.color} / {item.size}</div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div><div className="text-wmuted">Sumber</div><div className="text-cocoa">{item.sources.join(", ")}</div></div>
                <div><div className="text-wmuted">Jumlah</div><div className="font-semibold text-status-passed">{item.qty} pcs</div></div>
                <div><div className="text-wmuted">Referensi</div><div className="text-cocoa">{item.transfer_count}</div></div>
                <div><div className="text-wmuted">Terakhir</div><div className="text-cocoa">{fmtDate(item.last_transfer_at)}</div></div>
              </div>
            </div>
          ))}
          {!autoTransfers.items.length && <div className="text-center text-wmuted text-sm py-5" data-testid="auto-transfer-mobile-empty">Tidak ada auto transfer Gudang Baru → Gudang Utama pada periode ini.</div>}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead>Sumber Otomatis</TableHead><TableHead className="text-center">Jumlah Transfer</TableHead><TableHead className="text-center">Referensi</TableHead><TableHead>Transfer Terakhir</TableHead></TableRow></TableHeader>
            <TableBody>
              {autoTransfers.items.map((item) => <TableRow key={`${item.variant_id}-${item.product_id}`} data-testid={`auto-transfer-row-${item.variant_id}`}><TableCell className="font-medium text-cocoa">{item.product_name}</TableCell><TableCell>{item.color}/{item.size}</TableCell><TableCell>{item.sources.join(", ")}</TableCell><TableCell className="text-center font-semibold text-status-passed">{item.qty} pcs</TableCell><TableCell className="text-center">{item.transfer_count}</TableCell><TableCell className="text-sm text-wmuted">{fmtDate(item.last_transfer_at)}</TableCell></TableRow>)}
              {!autoTransfers.items.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-8" data-testid="auto-transfer-empty">Tidak ada auto transfer Gudang Baru → Gudang Utama pada periode ini.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <Panel>
        <div className="flex items-center justify-between p-4 flex-wrap gap-3 border-b border-taupe/15">
          <div className="flex items-center gap-2">
            {write && <Button onClick={() => navigate("/transfers/create")} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="transfer-create-btn"><Plus className="w-4 h-4 mr-1.5" />Create</Button>}
            <Button onClick={() => setExportOpen(true)} className="bg-status-passed hover:bg-status-passed/90 text-white" data-testid="transfer-export-btn"><FileDown className="w-4 h-4 mr-1.5" />Export</Button>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Label className="text-wmuted">From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9 bg-white w-40" data-testid="transfer-from-date" />
            <Label className="text-wmuted">To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9 bg-white w-40" data-testid="transfer-to-date" />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12">No</TableHead><TableHead>Date</TableHead><TableHead>Transfer From</TableHead>
              <TableHead>Transfer To</TableHead><TableHead>Keep</TableHead><TableHead>Status</TableHead><TableHead className="text-center">Total Items</TableHead>
              <TableHead>Updated at</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.slice((page - 1) * pageSize, page * pageSize).map((t, i) => (
              <TableRow key={t.id} data-testid={`transfer-row-${t.id}`}>
                <TableCell className="text-wmuted">{(page - 1) * pageSize + i + 1}</TableCell>
                <TableCell className="text-sm">{fmtDate(t.created_at)}</TableCell>
                <TableCell className="text-sm">{t.from_warehouse_name}</TableCell>
                <TableCell className="text-sm">{t.to_warehouse_name}</TableCell>
                <TableCell>
                  {t.from_keep && <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full" data-testid={`transfer-keep-badge-${t.id}`}>Keep Product</span>}
                  {t.fulfillment_only && <span className="ml-1 text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`transfer-fulfillment-badge-${t.id}`}>Transfer Fisik</span>}
                </TableCell>
                <TableCell>{(t.status || "Completed") === "Pending" ? <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`transfer-status-${t.id}`}>Belum Disiapkan</span> : <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid={`transfer-status-${t.id}`}>Selesai</span>}</TableCell>
                <TableCell className="text-center font-medium">{t.total_items}</TableCell>
                <TableCell className="text-sm text-wmuted">{fmtDate(t.updated_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(t)} className="h-8 w-8 text-blue-600" data-testid={`transfer-view-${t.id}`}><Eye className="w-4 h-4" /></Button>
                  {write && (t.status || "Completed") === "Pending" && <Button size="sm" onClick={() => confirmPrepare(t)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`transfer-confirm-${t.id}`}>Tandai Disiapkan</Button>}
                  {write && t.kind === "manual" && <Button size="icon" variant="ghost" onClick={() => setEditDoc(t)} className="h-8 w-8 text-cocoa" data-testid={`transfer-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>}
                  {write && t.kind === "manual" && <Button size="icon" variant="ghost" onClick={() => del(t)} className="h-8 w-8" data-testid={`transfer-del-${t.id}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-10"><ArrowRightLeft className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada transfer.</TableCell></TableRow>}
          </TableBody>
        </Table>
        {list.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-taupe/15 text-sm flex-wrap gap-2">
            <div className="flex items-center gap-2 text-wmuted">
              <span>Tampilkan</span>
              <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }} className="border border-taupe/30 rounded px-2 py-1 bg-white" data-testid="transfer-page-size">
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>Menampilkan {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, list.length)} dari {list.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-8 border-taupe text-cocoa" data-testid="transfer-prev">Sebelumnya</Button>
              <span className="text-cocoa">Hal {page} / {Math.max(1, Math.ceil(list.length / pageSize))}</span>
              <Button size="sm" variant="outline" disabled={page >= Math.ceil(list.length / pageSize)} onClick={() => setPage((p) => p + 1)} className="h-8 border-taupe text-cocoa" data-testid="transfer-next">Berikutnya</Button>
            </div>
          </div>
        )}
      </Panel>

      {detail && <DetailDialog t={detail} onClose={() => setDetail(null)} />}
      {editDoc && <EditDialog t={editDoc} onClose={() => setEditDoc(null)} onDone={reload} />}
      <ExportDialog open={exportOpen} setOpen={setExportOpen} onExport={doExport} />
    </div>
  );
}

function NeedPanel({ group, write, onProcess }) {
  const rowKey = (n) => `${n.variant_id}|${n.from_warehouse}|${n.to_warehouse}|${n.is_reminder ? "r" : "k"}`;
  const [sel, setSel] = useState({});
  const [q, setQ] = useState("");
  useEffect(() => { setSel({}); }, [group]);
  const filtered = q.trim()
    ? group.rows.filter((n) => `${n.product_name} ${n.color} ${n.size}`.toLowerCase().includes(q.trim().toLowerCase()))
    : group.rows;
  const allChecked = filtered.length > 0 && filtered.every((n) => sel[rowKey(n)]);
  const toggleAll = () => setSel((s) => {
    const next = { ...s };
    if (allChecked) filtered.forEach((n) => { delete next[rowKey(n)]; });
    else filtered.forEach((n) => { next[rowKey(n)] = true; });
    return next;
  });
  const toggle = (n, c) => setSel((s) => ({ ...s, [rowKey(n)]: c }));
  const chosen = group.rows.filter((n) => sel[rowKey(n)]);
  const chosenRefs = chosen.flatMap((n) => n.refs || []);
  const tid = `need-panel-${group.to_warehouse}`;

  return (
    <Panel className="p-0 overflow-hidden" data-testid={tid}>
      <div className="flex items-center justify-between px-4 py-3 bg-taupe/10 border-b border-taupe/15">
        <span className="font-semibold text-cocoa">Ke: <span className="text-status-passed">{group.to_warehouse_name}</span> <span className="text-wmuted">({group.rows.length})</span></span>
        {write && <Button size="sm" disabled={!chosen.length} className="bg-status-passed hover:bg-status-passed/90 text-white disabled:opacity-50"
          onClick={() => onProcess(chosenRefs)} data-testid={`${tid}-btn`}>Proses Transfer{chosen.length ? ` (${chosen.length})` : ""}</Button>}
      </div>
      <div className="px-4 py-2 border-b border-taupe/10">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk / warna / ukuran..." className="h-9 bg-white" data-testid={`${tid}-search`} />
      </div>
      <div className="max-h-80 overflow-y-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} disabled={!filtered.length} data-testid={`${tid}-selectall`} /></TableHead>
            <TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead className="text-center">Qty</TableHead><TableHead>Dari</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((n) => (
              <TableRow key={rowKey(n)} data-testid={`${tid}-row-${n.variant_id}`}>
                <TableCell><Checkbox checked={!!sel[rowKey(n)]} onCheckedChange={(c) => toggle(n, !!c)} data-testid={`${tid}-check-${n.variant_id}`} /></TableCell>
                <TableCell className="text-sm font-medium text-cocoa">{n.product_name}{n.is_reminder && <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded" title="Sudah terjual, perlu dipindah fisik">Terjual</span>}</TableCell>
                <TableCell className="text-sm">{n.color}/{n.size}</TableCell>
                <TableCell className="text-center font-bold text-amber-600">{n.qty}</TableCell>
                <TableCell className="text-sm text-cocoa">{n.from_warehouse_name}</TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-6 text-sm">{q ? "Tidak ditemukan." : "Tidak ada kebutuhan."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
}

function DetailDialog({ t, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-3">{t.number} <StatusBadge status={t.status} /></DialogTitle></DialogHeader>
        <div className="text-sm mb-2">{t.from_warehouse_name} → {t.to_warehouse_name}{t.from_keep && <span className="ml-2 text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Keep Product</span>}</div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Color</TableHead><TableHead>Size</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
          <TableBody>{t.items.map((it, i) => <TableRow key={i}><TableCell>{it.product_name}</TableCell><TableCell>{it.color}</TableCell><TableCell>{it.size}</TableCell><TableCell className="text-right font-semibold">{it.qty}</TableCell></TableRow>)}</TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

function ItemEditor({ flat, items, setItems }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2"><Label>Item</Label><Button size="sm" variant="outline" onClick={() => setItems([...items, { variant_id: "", qty: 1 }])} className="border-taupe text-cocoa">+ Item</Button></div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_90px_auto] gap-2 mb-2 items-center">
          <VariantSelect flat={flat} value={it.variant_id} onChange={(v) => { const n = [...items]; n[i].variant_id = v; setItems(n); }} testId={`transfer-variant-${i}`} />
          <Input type="number" value={it.qty} onChange={(e) => { const n = [...items]; n[i].qty = e.target.value; setItems(n); }} />
          <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-status-reject" /></Button>
        </div>
      ))}
    </div>
  );
}

function EditDialog({ t, onClose, onDone }) {
  const { flat } = useVariants();
  const [items, setItems] = useState((t.items || []).map((it) => ({ variant_id: it.variant_id, qty: it.qty })));

  const save = async () => {
    const payload = { items: items.filter((it) => it.variant_id).map((it) => {
      const f = flat.find((x) => x.variant_id === it.variant_id) || {};
      return { product_id: f.product_id, variant_id: it.variant_id, product_name: f.product_name, color: f.color, size: f.size, qc_status: "passed", qty: parseInt(it.qty) || 0 };
    }) };
    if (!payload.items.length) return toast.error("Item tidak boleh kosong");
    try { await api.put(`/transfers/${t.id}`, payload); toast.success("Transfer diperbarui"); onClose(); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Edit Transfer {t.number}</DialogTitle></DialogHeader>
        <div className="text-sm text-wmuted mb-1">{t.from_warehouse_name} → {t.to_warehouse_name}</div>
        <ItemEditor flat={flat} items={items} setItems={setItems} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-edit-transfer-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan Perubahan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ExportDialog({ open, setOpen, onExport }) {
  const [mode, setMode] = useState("all");
  const [warehouses, setWarehouses] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  useEffect(() => {
    if (open) {
      api.get("/warehouses", { params: { usage: "produk_jadi" } }).then((r) => setWarehouses(r.data)).catch(() => {});
      setMode("all"); setFrom(""); setTo(""); setStart(""); setEnd("");
    }
  }, [open]);

  const submit = () => {
    if (mode === "specific" && (!from || !to)) return toast.error("Pilih gudang asal & tujuan");
    onExport({ mode, from, to, start, end }); setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="export-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Rekap Pengiriman Product</DialogTitle>
          <DialogDescription>Export daftar pengiriman/transfer ke Excel sesuai filter.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-6 mb-1">
          <label className="flex items-center gap-2 text-sm text-cocoa cursor-pointer">
            <input type="radio" checked={mode === "all"} onChange={() => setMode("all")} data-testid="export-mode-all" /> Semua Stock
          </label>
          <label className="flex items-center gap-2 text-sm text-cocoa cursor-pointer">
            <input type="radio" checked={mode === "specific"} onChange={() => setMode("specific")} data-testid="export-mode-specific" /> Stok Tertentu
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className={mode === "all" ? "text-taupe" : ""}>Transfer From</Label>
            <Select value={from || undefined} onValueChange={setFrom} disabled={mode === "all"}>
              <SelectTrigger className="bg-white" data-testid="export-from"><SelectValue placeholder="Pilih gudang asal" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className={mode === "all" ? "text-taupe" : ""}>Transfer To</Label>
            <Select value={to || undefined} onValueChange={setTo} disabled={mode === "all"}>
              <SelectTrigger className="bg-white" data-testid="export-to"><SelectValue placeholder="Pilih gudang tujuan" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Start Date</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="export-start" /></div>
          <div><Label>End Date</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="export-end" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Cancel</Button>
          <Button onClick={submit} className="bg-status-passed hover:bg-status-passed/90 text-white" data-testid="export-excel-btn"><FileDown className="w-4 h-4 mr-1.5" />Excel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
